/**
 * KOSIMA: back-in-stock capture.
 *
 * Progressive enhancement. The markup is a native Shopify customer form that works with no
 * JavaScript at all -- it posts, creates a tagged subscriber, and reloads with a success banner.
 *
 * When a Klaviyo public site ID is present on the element, this intercepts the submit and posts to
 * Klaviyo's client-side back-in-stock endpoint instead, so the request lands in Klaviyo rather than
 * Shopify Customers. The endpoint takes only a PUBLIC site ID, which is why this is safe in a theme.
 *
 * API shape per https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 */
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions';
const KLAVIYO_REVISION = '2026-07-15';

class NotifyMeForm extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Delegated from the host, not the form: the form node is replaced on every variant change, and
    // "submit" bubbles, so this binding survives those swaps without re-binding.
    this.addEventListener('submit', this.onSubmit.bind(this), { signal });

    // <product-rerender> runs in partial mode on the product section and only swaps a hardcoded
    // allowlist of block types, which does not include this one. So keep ourselves in sync by
    // listening to the same event it does and pulling our own replacement out of the fragment.
    const observed = this.getAttribute('observe-form');
    const form = observed && document.forms[observed];

    if (form) {
      form.addEventListener('product:rerender', this.onProductRerender.bind(this), { signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  onProductRerender(event) {
    const fragment = event.detail?.htmlFragment;
    if (!fragment || !this.id) return;

    // The fragment is a detached tree; querySelector works on both Element and DocumentFragment.
    const replacement =
      fragment.querySelector?.(`#${CSS.escape(this.id)}`) ??
      fragment.getElementById?.(this.id);

    if (!replacement) return;

    // Swap contents rather than the node itself, so the listeners bound above stay alive.
    this.innerHTML = replacement.innerHTML;

    for (const name of ['klaviyo-company-id', 'klaviyo-variant-id']) {
      const value = replacement.getAttribute(name);
      if (value === null) this.removeAttribute(name);
      else this.setAttribute(name, value);
    }
  }

  get feedback() {
    return this.querySelector('.notify-me__feedback');
  }

  say(message, isError) {
    if (!this.feedback) return;
    this.feedback.textContent = message;
    this.feedback.hidden = false;
    this.feedback.classList.toggle('text-error', Boolean(isError));
  }

  async onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const companyId = this.getAttribute('klaviyo-company-id');
    const variantId = this.getAttribute('klaviyo-variant-id');

    // No Klaviyo configured: let the native Shopify form post as normal.
    if (!companyId || !variantId) return;

    const email = form.querySelector('input[type="email"]')?.value?.trim();
    if (!email) return; // let native validation handle it

    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button) button.disabled = true;

    try {
      const response = await fetch(
        `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: { data: { type: 'profile', attributes: { email } } },
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: variantId } },
              },
            },
          }),
        }
      );

      // 202 Accepted is the documented success response.
      if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);

      this.say("Thanks — we'll email you when this is back in stock.", false);
      form.reset();
    } catch (error) {
      // Do not strand the shopper: fall back to the native Shopify form so the email is still
      // captured, rather than silently losing the request.
      console.warn('[notify-me] Klaviyo subscription failed, falling back to Shopify form:', error);
      if (button) button.disabled = false;
      form.submit();
      return;
    }

    if (button) button.disabled = false;
  }
}

if (!window.customElements.get('notify-me-form')) {
  window.customElements.define('notify-me-form', NotifyMeForm);
}
